<template>
  <v-container
    v-if="schema"
    class="mxw-800"
  >
    <v-card
      border
      flat
      tile
    >
      <v-card-text class="py-0 mnh-250">
        <template v-if="questions.length > 0">
          <QuestionSet
            v-model="survey"
            @back="backFromQuestion($event)"
            @next="forwardFromQuestion($event)"
            :processing="processing"
            :questions="questions"
            :reversible="index > 0"
            :schema="schema.definition"
            :section="section"
            :transition-name="transitionName"
            key-name="question"
          />
        </template>

        <template v-else>
          <v-progress-linear
            class="my-8"
            indeterminate
          />
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const { api: Api } = useApi();
const eventBus = useEventBus();
const { locale } = useI18n();
const route = useRoute();
const router = useRouter();

const index = ref(0);
const processing = ref(false);
const questions = ref([]);
const schema = ref(null);
const schemaId = computed(() => schema.value?.id);
const section = ref(null);
const survey = ref({ custom: {} });
const transitionName = ref('slide-left');

async function backFromQuestion(index) {
  processing.value = true;
  await loadQuestions();

  if (index === 0) {
    processing.value = false;
  } else {
    await goToQuestion(index - 1);
  }
}

async function forwardFromQuestion(index) {
  processing.value = true;
  await loadQuestions();

  if (index === questions.value.length - 1) {
    await submitSurvey();
  } else {
    await goToQuestion(index + 1);
  }
}

async function goToQuestion(newIndex) {
  processing.value = false;
  section.value = null;
  index.value = newIndex;

  setTimeout(() => {
    section.value = `question-${newIndex}`;
  }, 600);
}

async function load() {
  await loadSchema();

  const memberParams = {
    default_locale: locale.value,
    is_anonymous: true,
    organization_id: window.organization_id,
    is_parent: true,
  };

  const resp = await Api.member.create(memberParams);

  if (resp) {
    await loadQuestions();
    section.value = 'question-0';
  }
}

async function loadQuestions() {
  const resp = await Api.shared.questionSet.index({
    owner_id: schemaId.value,
    owner_type: 'Schema',
    draft_data: survey.value,
  });

  questions.value = resp.data;
}

async function loadSchema() {
  const resp = await Api.publicApi.organization.schema
    .get(route.params.schemaId)
    .catch(() => eventBus.longChime('Survey could not be found'));

  if (!resp) return;

  schema.value = resp.data;
}

async function submitSurvey() {
  survey.value.schema_id = schema.value.id;

  const resp = await Api.parent.survey.create(survey.value).catch((error) => eventBus.error(error));

  processing.value = false;
  if (!resp) return;

  processing.value = false;

  if (schema.value.meta.submitted_redirect_url)
    window.location.replace(schema.value.meta.submitted_redirect_url);
  else await router.push({ name: 'Dashboard' });
}

onMounted(load);
</script>
